<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Prescription Management' : 'Deleted Prescriptions'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all prescriptions' : 'Restore or permanently delete prescriptions'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/prescriptions/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Prescription
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Prescriptions</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Prescriptions"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Symptoms, diagnosis, tips..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), refreshAll()
                    "
                    @keydown.enter="refreshAll()"
                  ></v-text-field>
                </v-col>

                <!-- Doctor Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.doctor_id"
                    :items="doctors"
                    item-text="text"
                    item-value="value"
                    label="Doctor"
                    prepend-inner-icon="mdi-doctor"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="refreshAll()"
                  ></v-autocomplete>
                </v-col>

                <!-- Patient Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.patient_id"
                    :items="patients"
                    item-text="text"
                    item-value="value"
                    label="Patient"
                    prepend-inner-icon="mdi-account-heart"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="refreshAll()"
                  ></v-autocomplete>
                </v-col>

                <!-- Consultation Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.consultation_id"
                    :items="consultations"
                    item-text="text"
                    item-value="value"
                    label="Consultation"
                    prepend-inner-icon="mdi-medical-bag"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="refreshAll()"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="refreshAll()"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <!-- Date Filters -->
              <v-row class="mt-2" dense>
                <v-col cols="12" md="auto">
                  <div class="d-flex align-center flex-wrap" style="gap:6px">
                    <span class="text-caption grey--text mr-1">Date:</span>
                    <v-chip small :color="datePreset === 'today' ? 'primary' : ''" :outlined="datePreset !== 'today'" @click="setDatePreset('today')">Today</v-chip>
                    <v-chip small :color="datePreset === 'yesterday' ? 'primary' : ''" :outlined="datePreset !== 'yesterday'" @click="setDatePreset('yesterday')">Yesterday</v-chip>
                    <v-chip small :color="datePreset === 'this_week' ? 'primary' : ''" :outlined="datePreset !== 'this_week'" @click="setDatePreset('this_week')">This Week</v-chip>
                    <v-chip small :color="datePreset === 'this_month' ? 'primary' : ''" :outlined="datePreset !== 'this_month'" @click="setDatePreset('this_month')">This Month</v-chip>
                    <v-chip small :color="datePreset === 'custom' ? 'primary' : ''" :outlined="datePreset !== 'custom'" @click="datePreset = 'custom'">Custom</v-chip>
                    <v-chip v-if="datePreset" small color="error" outlined @click="clearDateFilter"><v-icon x-small left>mdi-close</v-icon>Clear</v-chip>
                  </div>
                </v-col>

                <v-col cols="12" md="5" v-if="datePreset === 'custom'">
                  <div class="d-flex align-center" style="gap:8px">
                    <v-menu v-model="fromMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="filter.start_date" label="From" prepend-inner-icon="mdi-calendar" readonly outlined dense hide-details clearable v-bind="attrs" v-on="on" @click:clear="(filter.start_date = ''), refreshAll()"></v-text-field>
                      </template>
                      <v-date-picker v-model="filter.start_date" no-title @input="fromMenu = false; refreshAll()"></v-date-picker>
                    </v-menu>
                    <v-menu v-model="toMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="filter.end_date" label="To" prepend-inner-icon="mdi-calendar" readonly outlined dense hide-details clearable v-bind="attrs" v-on="on" @click:clear="(filter.end_date = ''), refreshAll()"></v-text-field>
                      </template>
                      <v-date-picker v-model="filter.end_date" no-title @input="toMenu = false; refreshAll()"></v-date-picker>
                    </v-menu>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Prescription Analytics -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="pb-2 pt-3 px-4">
              <v-icon class="mr-2" color="primary" size="20">mdi-chart-box-outline</v-icon>
              <span class="text-body-1 font-weight-medium">Prescription Analytics</span>
            </v-card-title>
            <v-divider class="mx-4"></v-divider>
            <v-card-text class="px-4 pb-4">
              <!-- Stat Cards -->
              <v-row dense>
                <v-col cols="12" sm="4" v-for="card in statCards" :key="card.title">
                  <DashboardWidget dense :title="card.title" :value="card.value" :icon="card.icon" :color="card.color" :loading="isDashboardLoading" />
                </v-col>
              </v-row>

              <!-- Doctor-wise / Category-wise -->
              <v-row dense class="mt-2">
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3">
                    <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">DOCTOR-WISE PRESCRIPTIONS</div>
                    <div style="height:240px;position:relative;">
                      <canvas ref="doctorWiseChart"></canvas>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3">
                    <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">CATEGORY-WISE PRESCRIPTIONS</div>
                    <div style="height:240px;position:relative;">
                      <canvas ref="categoryWiseChart"></canvas>
                    </div>
                  </v-card>
                </v-col>

                <!-- Daily / Monthly Trend -->
                <v-col cols="12">
                  <v-card outlined class="pa-3">
                    <div class="d-flex align-center justify-space-between mb-2 flex-wrap">
                      <div class="text-caption font-weight-bold grey--text text--darken-1">PRESCRIPTIONS ISSUED — TREND</div>
                      <v-btn-toggle v-model="trendGranularity" dense mandatory color="primary">
                        <v-btn small value="daily">Daily</v-btn>
                        <v-btn small value="monthly">Monthly</v-btn>
                      </v-btn-toggle>
                    </div>
                    <div style="height:240px;position:relative;">
                      <canvas ref="trendChart"></canvas>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Prescriptions Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Prescriptions List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-prescription</v-icon>
                      {{ count }}
                      {{ count === 1 ? "Prescription" : "Prescriptions" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && prescriptions.length > 0"
                      x-small
                      color="success"
                      outlined
                      @click="generateExcel"
                      class="px-2"
                      :loading="isExcelloading"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading prescriptions..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="prescriptions"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Prescription ID Column -->
                <template #[`item.prescription_id`]="{ item }">
                  <span class="font-weight-medium">#{{ item.id }}</span>
                </template>

                <!-- Doctor Column -->
                <template #[`item.doctor`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="28" class="mr-2" color="blue">
                      <v-icon color="white" size="16">mdi-doctor</v-icon>
                    </v-avatar>
                    <span>{{ item.doctor ? item.doctor.user_name : "-" }}</span>
                  </div>
                </template>

                <!-- Patient Column -->
                <template #[`item.patient`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="28" class="mr-2" color="green">
                      <v-icon color="white" size="16">mdi-account-heart</v-icon>
                    </v-avatar>
                    <span>{{
                      item.patient ? item.patient.user_name : "-"
                    }}</span>
                  </div>
                </template>

                <!-- Consultation ID Column -->
                <template #[`item.consultation_id_col`]="{ item }">
                  <v-tooltip v-if="item.consultation_id" bottom>
                    <template #activator="{ on, attrs }">
                      <v-chip v-bind="attrs" v-on="on" color="info" x-small outlined>
                        {{ item.consultation && item.consultation.invoice_no ? item.consultation.invoice_no : "#" + item.consultation_id }}
                      </v-chip>
                    </template>
                    <span>Consultation No. — ID #{{ item.consultation_id }}</span>
                  </v-tooltip>
                  <span v-else class="text-caption grey--text">N/A</span>
                </template>

                <!-- Category Column (derived from doctor's pathy) -->
                <template #[`item.category`]="{ item }">
                  <v-chip v-if="item.doctor && item.doctor.pathy" color="purple" x-small outlined>
                    {{ item.doctor.pathy.description }}
                  </v-chip>
                  <span v-else class="text-caption grey--text">—</span>
                </template>

                <!-- Appointment ID Column (via consultation) -->
                <template #[`item.appointment_id_col`]="{ item }">
                  <v-tooltip v-if="item.consultation && item.consultation.appointment_id" bottom>
                    <template #activator="{ on, attrs }">
                      <v-chip v-bind="attrs" v-on="on" color="teal" x-small outlined>
                        {{ item.consultation.appointment && item.consultation.appointment.order_number ? item.consultation.appointment.order_number : "#" + item.consultation.appointment_id }}
                      </v-chip>
                    </template>
                    <span>Appointment Order No. — ID #{{ item.consultation.appointment_id }}</span>
                  </v-tooltip>
                  <span v-else class="text-caption grey--text">N/A</span>
                </template>

                <!-- Medicines Count Column -->
                <template #[`item.medicines_count`]="{ item }">
                  <v-chip small color="orange" outlined>
                    <v-icon x-small left>mdi-pill</v-icon>
                    {{ item.prescription_items_count ?? (item.prescription_items ? item.prescription_items.length : 0) }}
                  </v-chip>
                </template>

                <!-- Date Column -->
                <template #[`item.created_at`]="{ item }">
                  <div class="text-caption">
                    <div>{{ item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN') : '—' }}</div>
                    <div class="grey--text">{{ item.created_at ? new Date(item.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '' }}</div>
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- Download PDF Button -->
                      <!-- View Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="blue-grey darken-1"
                            :to="`/prescriptions/${encryptIt(item.id)}/details`"
                            class="mr-1"
                            dark
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-eye</v-icon>
                          </v-btn>
                        </template>
                        <span>View Prescription</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="indigo"
                            class="mr-1"
                            dark
                            :loading="downloadingId === item.id"
                            v-bind="attrs"
                            v-on="on"
                            @click="downloadPdf(item)"
                          >
                            <v-icon x-small>mdi-file-pdf-box</v-icon>
                          </v-btn>
                        </template>
                        <span>Download PDF</span>
                      </v-tooltip>

                      <!-- Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/prescriptions/${encryptIt(item.id)}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit Prescription</span>
                      </v-tooltip>

                      <!-- Delete Button (Admin / Super Admin only) -->
                      <v-tooltip bottom v-if="canDeletePrescription">
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="showDeleteConfirmation(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Prescription</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="Restore_this(item.id)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Prescription</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-prescription</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No Prescriptions Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No prescriptions match your current filters. Try adjusting your search criteria."
                          : "You haven't added any prescriptions yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/prescriptions/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Prescription
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Success Dialog -->
      <v-dialog v-model="is_deleted_prescription" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Prescription deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_prescription = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Restore Success Dialog -->
      <v-dialog v-model="is_restored_prescription" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Prescription restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_prescription = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this prescription?
              </h3>

              <div
                class="prescription-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="primary">
                    <v-icon color="white" size="24">mdi-prescription</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      Prescription #{{ prescriptionToDelete ? prescriptionToDelete.id : "" }}
                    </div>
                    <div class="text-caption grey--text">
                      Doctor: {{ prescriptionToDelete && prescriptionToDelete.doctor ? prescriptionToDelete.doctor.user_name : "N/A" }}
                    </div>
                    <div class="text-caption grey--text">
                      Patient: {{ prescriptionToDelete && prescriptionToDelete.patient ? prescriptionToDelete.patient.user_name : "N/A" }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the prescription
                to the trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later from the deleted prescriptions section.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDelete"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmDelete"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Prescription
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { mapGetters } from "vuex";
import Chart from "chart.js/auto";
import DashboardWidget from "@/components/DashboardWidget.vue";

export default {
  name: "PrescriptionsPage",
  components: { DashboardWidget },
  data() {
    return {
      filter: {
        search_keyword: "",
        doctor_id: "",
        patient_id: "",
        consultation_id: "",
        is_active: "",
        start_date: "",
        end_date: "",
        show_deleted: false,
      },
      datePreset: "",
      fromMenu: false,
      toMenu: false,
      isDashboardLoading: false,
      dashboardStats: {},
      dashboardDaily: [],
      dashboardMonthly: [],
      trendGranularity: "daily",
      charts: { doctorWise: null, categoryWise: null, trend: null },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      jumpToPage: 1,
      showDeleteDialog: false,
      prescriptionToDelete: null,
      isDeleting: false,
      is_deleted_prescription: false,
      is_restored_prescription: false,
      prescriptions: [],
      doctors: [
        {
          id: 0,
          text: "Select Doctor",
          value: "",
        },
      ],
      patients: [
        {
          id: 0,
          text: "Select Patient",
          value: "",
        },
      ],
      consultations: [
        {
          id: 0,
          text: "Select Consultation",
          value: "",
        },
      ],
      statuses: [
        {
          id: 0,
          text: "Select Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        {
          text: "Prescription ID",
          value: "prescription_id",
          sortable: false,
          width: "100px",
        },
        {
          text: "Doctor",
          value: "doctor",
          sortable: false,
          width: "150px",
        },
        {
          text: "Patient",
          value: "patient",
          sortable: false,
          width: "150px",
        },
        {
          text: "Consultation ID",
          value: "consultation_id_col",
          sortable: false,
          width: "120px",
        },
        {
          text: "Category",
          value: "category",
          sortable: false,
          width: "120px",
        },
        {
          text: "Appointment ID",
          value: "appointment_id_col",
          sortable: false,
          width: "120px",
        },
        {
          text: "Medicines",
          value: "medicines_count",
          sortable: false,
          width: "100px",
          align: "center",
        },
        {
          text: "Issued On",
          value: "created_at",
          sortable: false,
          width: "110px",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "170px",
          align: "center",
        },
      ],
      isLoading: false,
      isExcelloading: false,
      downloadingId: null,
    };
  },

  computed: {
    ...mapGetters("auth", ["isSuperAdmin", "isAdmin"]),
    canDeletePrescription() {
      return this.isSuperAdmin || this.isAdmin;
    },
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.doctor_id ||
        this.filter.patient_id ||
        this.filter.consultation_id ||
        this.filter.is_active ||
        this.filter.start_date ||
        this.filter.end_date
      );
    },
    statCards() {
      const s = this.dashboardStats;
      return [
        { title: "Prescriptions Issued", value: s.total_issued || 0, icon: "mdi-prescription", color: "primary" },
        { title: "Issued Today", value: s.today || 0, icon: "mdi-calendar-today", color: "info" },
        { title: "Issued This Month", value: s.this_month || 0, icon: "mdi-calendar-month", color: "secondary" },
      ];
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
    trendGranularity() {
      this.renderTrendChart();
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    this.getData();
    this.getMasters();
    this.fetchDashboard();
  },

  beforeDestroy() {
    Object.values(this.charts).forEach((c) => c && c.destroy());
  },

  methods: {
    async downloadPdf(item) {
      this.downloadingId = item.id;
      try {
        const response = await axios.get(`/prescriptions/${this.encryptIt(item.id)}/pdf`, {
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `prescription_${item.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$toast.error('Failed to download prescription PDF');
      } finally {
        this.downloadingId = null;
      }
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=prescriptions");
        if (response.data?.users) {
          this.doctors = [{ id: 0, text: "Select Doctor", value: "" }];
          this.patients = [{ id: 0, text: "Select Patient", value: "" }];
          
          const doctorUsers = response.data.users.filter(
            (user) =>
              user.position &&
              user.position.name &&
              user.position.name.toLowerCase().includes("doctor")
          );

          const patientUsers = response.data.users.filter(
            (user) =>
              user.position &&
              user.position.name &&
              user.position.name.toLowerCase().includes("patient")
          );

          doctorUsers.forEach((user) => {
            this.doctors.push({
              text: user.first_name,
              value: user.id,
            });
          });

          patientUsers.forEach((user) => {
            this.patients.push({
              text: user.first_name,
              value: user.id,
            });
          });
        }
        
        if (response.data?.consultations) {
          this.consultations = [{ id: 0, text: "Select Consultation", value: "" }];
          this.consultations.push(...response.data.consultations.map(
            (consultation) => ({
              text: consultation.title,
              value: consultation.id,
            })
          ));
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    async getData() {
      console.log("Get Prescriptions Data");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      // Ensure all filter values are properly set
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.doctor_id = this.filter.doctor_id || "";
      this.filter.patient_id = this.filter.patient_id || "";
      this.filter.consultation_id = this.filter.consultation_id || "";
      this.filter.is_active = this.filter.is_active || "";
      this.filter.start_date = this.filter.start_date || "";
      this.filter.end_date = this.filter.end_date || "";

      let query =
        "context=panel" +
        "&page=" +
        this.page +
        "&rowsPerPage=" +
        this.itemsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&doctor_id=" +
        this.filter.doctor_id +
        "&patient_id=" +
        this.filter.patient_id +
        "&consultation_id=" +
        this.filter.consultation_id +
        "&start_date=" +
        this.filter.start_date +
        "&end_date=" +
        this.filter.end_date +
        "&search_keyword=" +
        this.filter.search_keyword;

      await axios
        .get(`prescriptions?${query}`)
        .then((response) => {
          this.prescriptions = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
          this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    refreshAll() {
      this.page = 1;
      this.getData();
      this.fetchDashboard();
    },

    async fetchDashboard() {
      this.isDashboardLoading = true;
      const query = new URLSearchParams({
        search_keyword: this.filter.search_keyword || "",
        doctor_id: this.filter.doctor_id || "",
        patient_id: this.filter.patient_id || "",
        consultation_id: this.filter.consultation_id || "",
        is_active: this.filter.is_active || "",
        start_date: this.filter.start_date || "",
        end_date: this.filter.end_date || "",
      }).toString();

      try {
        const { data } = await axios.get(`prescriptions/dashboard?${query}`);
        this.dashboardStats = data.data.stats;
        this.dashboardDaily = data.data.charts.daily || [];
        this.dashboardMonthly = data.data.charts.monthly || [];
        this.$nextTick(() => {
          this.renderDoctorAndCategoryCharts(data.data.charts);
          this.renderTrendChart();
        });
      } catch (error) {
        console.error("Failed to load prescription dashboard:", error);
      }
      this.isDashboardLoading = false;
    },

    renderDoctorAndCategoryCharts(charts) {
      if (this.charts.doctorWise) this.charts.doctorWise.destroy();
      if (this.charts.categoryWise) this.charts.categoryWise.destroy();

      if (this.$refs.doctorWiseChart) {
        const doctorWise = charts.doctor_wise || [];
        this.charts.doctorWise = new Chart(this.$refs.doctorWiseChart.getContext("2d"), {
          type: "bar",
          data: {
            labels: doctorWise.map((r) => r.doctor),
            datasets: [{ label: "Prescriptions", data: doctorWise.map((r) => r.count), backgroundColor: "#1976D2" }],
          },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } },
        });
      }

      if (this.$refs.categoryWiseChart) {
        const categoryWise = charts.category_wise || [];
        this.charts.categoryWise = new Chart(this.$refs.categoryWiseChart.getContext("2d"), {
          type: "doughnut",
          data: {
            labels: categoryWise.map((r) => r.category),
            datasets: [{ data: categoryWise.map((r) => r.count), backgroundColor: ["#2196F3", "#FF9800", "#9C27B0", "#4CAF50", "#F44336", "#607D8B", "#00BCD4", "#795548"] }],
          },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "right", labels: { boxWidth: 10, font: { size: 10 } } } } },
        });
      }
    },

    renderTrendChart() {
      if (!this.$refs.trendChart) return;
      if (this.charts.trend) this.charts.trend.destroy();

      const source = this.trendGranularity === "monthly" ? this.dashboardMonthly : this.dashboardDaily;
      const labelKey = this.trendGranularity === "monthly" ? "month" : "date";

      this.charts.trend = new Chart(this.$refs.trendChart.getContext("2d"), {
        type: "line",
        data: {
          labels: source.map((r) => r[labelKey]),
          datasets: [{ label: "Prescriptions Issued", data: source.map((r) => r.count), borderColor: "#7B1FA2", backgroundColor: "rgba(123,31,162,0.1)", tension: 0.4, fill: true }],
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } },
      });
    },

    setDatePreset(preset) {
      this.datePreset = preset;
      const today = new Date();
      const fmt = (d) => d.toISOString().split("T")[0];
      if (preset === "today") {
        this.filter.start_date = fmt(today);
        this.filter.end_date = fmt(today);
      } else if (preset === "yesterday") {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        this.filter.start_date = fmt(y);
        this.filter.end_date = fmt(y);
      } else if (preset === "this_week") {
        const mon = new Date(today);
        const day = today.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        mon.setDate(today.getDate() + diff);
        this.filter.start_date = fmt(mon);
        this.filter.end_date = fmt(today);
      } else if (preset === "this_month") {
        this.filter.start_date = fmt(new Date(today.getFullYear(), today.getMonth(), 1));
        this.filter.end_date = fmt(today);
      }
      this.refreshAll();
    },

    clearDateFilter() {
      this.datePreset = "";
      this.filter.start_date = "";
      this.filter.end_date = "";
      this.refreshAll();
    },

    showDeleteConfirmation(prescription) {
      this.prescriptionToDelete = prescription;
      this.showDeleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.prescriptionToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.prescriptionToDelete) return;

      this.isDeleting = true;

      await axios
        .post(`/prescriptions/delete/${this.encryptIt(this.prescriptionToDelete.id)}`)
        .then(() => {
          this.showDeleteDialog = false;
          this.prescriptionToDelete = null;
          this.isDeleting = false;
          this.is_deleted_prescription = true;
          this.getData();
        })
        .catch((error) => {
          console.log(error);
          this.isDeleting = false;
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async Delete_this(id) {
      await axios
        .post(`/prescriptions/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.is_deleted_prescription = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async Restore_this(id) {
      await axios
        .post(`/prescriptions/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.is_restored_prescription = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async generateExcel() {
      this.isExcelloading = true;

      let query =
        "show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&doctor_id=" +
        this.filter.doctor_id +
        "&patient_id=" +
        this.filter.patient_id +
        "&consultation_id=" +
        this.filter.consultation_id +
        "&search_keyword=" +
        this.filter.search_keyword;

      try {
        const response = await axios.get(`prescriptions/excel?${query}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `prescriptions_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }

      this.isExcelloading = false;
    },

    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },


    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.doctor_id = "";
      this.filter.patient_id = "";
      this.filter.consultation_id = "";
      this.filter.is_active = "";
      this.filter.start_date = "";
      this.filter.end_date = "";
      this.datePreset = "";
      this.refreshAll();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.doctor_id) count++;
      if (this.filter.patient_id) count++;
      if (this.filter.consultation_id) count++;
      if (this.filter.is_active) count++;
      if (this.filter.start_date || this.filter.end_date) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        // Reset to current page if invalid input
        this.jumpToPage = this.page;
      }
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Delete Confirmation Dialog Styling */
.prescription-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.prescription-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>